import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  getUserDocuments,
  createDocument,
  updateDocument,
  deleteDocument as deleteDoc,
} from "@/shared/db";
import type { Database } from "@/shared/db";
import { useAuth } from "../contexts/AuthContext";

type Document = Database["public"]["Tables"]["documents"]["Row"];
type DocumentInsert = Database["public"]["Tables"]["documents"]["Insert"];
type DocumentUpdate = Database["public"]["Tables"]["documents"]["Update"];

export const useDocuments = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDocuments = async () => {
    if (!user) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const docs = await getUserDocuments(user.id);
      setDocuments(docs);
      setError(null);
    } catch (err) {
      setError(t("student.documents.errors.loadFailed"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => {
      loadDocuments();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  const addDocument = async (doc: DocumentInsert) => {
    try {
      const newDoc = await createDocument(doc);
      setDocuments((prev) => [newDoc, ...prev]);

      // Track document creation
      import("@/utils/analytics").then(({ analytics }) => {
        analytics.documentCreated(doc.document_type || "unknown");
      });

      return newDoc;
    } catch (err) {
      setError(t("student.documents.errors.createFailed"));
      console.error(err);
      throw err;
    }
  };

  const editDocument = async (id: string, updates: DocumentUpdate) => {
    try {
      const updatedDoc = await updateDocument(id, updates);
      setDocuments((prev) =>
        prev.map((doc) => (doc.id === id ? updatedDoc : doc))
      );
      return updatedDoc;
    } catch (err) {
      setError(t("student.documents.errors.updateFailed"));
      console.error(err);
      throw err;
    }
  };

  const deleteDocument = async (id: string) => {
    try {
      await deleteDoc(id);
      setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    } catch (err) {
      setError(t("student.documents.errors.deleteFailed"));
      console.error(err);
      throw err;
    }
  };

  return {
    documents,
    loading,
    error,
    loadDocuments,
    addDocument,
    editDocument,
    deleteDocument,
  };
};
